// Unpublished Work © 2023-2024 Deere & Company.
import DownloadIcon from "@mui/icons-material/Download";
import { Button, Grid } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import InvoiceDownloadButton from "./InvoiceDownloadButton";
import { TimeLimit } from "../../../../api/invoices/interfaces";
import { BusinessAction } from "../../../../api/rbac/types";
import { ConfigContext } from "../../../../context/ConfigContext";
import { DealerAccountContext } from "../../../../context/DealerAccountContext";
import { FeatureToggleContextV2 } from "../../../../context/FeatureToggleContextV2/FeatureToggleContextV2";
import { RBACAccessContext } from "../../../../context/RBACAccessContext/RBACAccessContext";
import { UserContext } from "../../../../context/UserContext/UserContext";
import {
  CHINA_DEALER_LOCATION,
  INDIA_DEALER_LOCATION,
} from "../../../../utils/constants";
import { rbacCheck } from "../../../../utils/rbacCheck";
import { getShowClaimStatus } from "../../../../utils/showClaims";
import { CATEGORIES, TOGGLES } from "../../../featureToggles/constants";

interface InvoiceLineItemsProps {
  isOrderInvoice: boolean;
  sendRef?: any;
  setPrintButtonClicked?: any;
  reversed?: boolean;
  timeLimit?: TimeLimit;
  isVendorDirect?: boolean;
  onDownloadToExcelClick?: () => void;
}

const InvoiceDetailsActionButtons = ({
  isOrderInvoice,
  sendRef,
  setPrintButtonClicked,
  reversed = false,
  timeLimit,
  isVendorDirect,
  onDownloadToExcelClick,
}: InvoiceLineItemsProps) => {
  const navigate = useNavigate();
  const { invoiceNumber } = useParams();
  const {
    ENABLE_INVOICE_DETAILS_V2,
    ENABLE_CLAIMS_INDIA,
    ENABLE_CLAIMS_CHINA,
    ENABLE_FEATURE_TOGGLE_CONTEXT_V2,
    ENABLE_CLAIMS,
    ENABLE_VENDOR_DIRECT_INVOICE,
  } = useContext(ConfigContext);
  const { isRBACEnabled } = useContext(RBACAccessContext);
  const { dealerAccount, dealerLocation } = useContext(DealerAccountContext);
  const user = useContext(UserContext);
  const { isToggleEnabled } = useContext(FeatureToggleContextV2);
  const { t } = useTranslation(["common", "claims", "invoices"]);
  const isChinaDealer = dealerLocation === CHINA_DEALER_LOCATION;
  const isIndiaDealer = dealerLocation === INDIA_DEALER_LOCATION;
  const [disableActions, setDisableActions] = useState<boolean>(true);
  const accountId = dealerAccount?.id as string;

  useEffect(() => {
    if (isChinaDealer) {
      setDisableActions(ENABLE_CLAIMS_CHINA);
    }
    if (isIndiaDealer) {
      setDisableActions(ENABLE_CLAIMS_INDIA);
    }
  }, []);

  const claimsTimeLimitExceeded = timeLimit?.claimsExceeded ?? false;

  const isClaimsByHUEnabled =
    ENABLE_FEATURE_TOGGLE_CONTEXT_V2 &&
    isToggleEnabled(CATEGORIES.CLAIMS_SERVICE, TOGGLES.ENABLE_CLAIMS_BY_HU);

  const isVendorDirectInvoice = ENABLE_VENDOR_DIRECT_INVOICE && isVendorDirect;

  const hasAccessToClaim = () => {
    const isClaimsEnabled =
      (ENABLE_FEATURE_TOGGLE_CONTEXT_V2 &&
        isToggleEnabled(CATEGORIES.CLAIMS_SERVICE, TOGGLES.ENABLE_CLAIMS) &&
        ENABLE_CLAIMS) ||
      (!ENABLE_FEATURE_TOGGLE_CONTEXT_V2 && ENABLE_CLAIMS);

    if (isRBACEnabled) {
      const isUserPermissionAllowed = rbacCheck(
        isRBACEnabled,
        user,
        BusinessAction.CREATE_CLAIM
      );
      return (
        isUserPermissionAllowed &&
        isClaimsEnabled &&
        (!isClaimsByHUEnabled || isVendorDirectInvoice)
      );
    }

    const isShowClaimsIsTrue = getShowClaimStatus();
    return (
      isShowClaimsIsTrue &&
      isClaimsEnabled &&
      (!isClaimsByHUEnabled || isVendorDirectInvoice)
    );
  };

  const shouldShowCreateClaimButton =
    isOrderInvoice &&
    hasAccessToClaim() &&
    !reversed &&
    disableActions &&
    !isClaimsByHUEnabled &&
    !claimsTimeLimitExceeded;

  const shouldShowCreateVendorClaimButton =
    isOrderInvoice &&
    hasAccessToClaim() &&
    !reversed &&
    disableActions &&
    isClaimsByHUEnabled &&
    isVendorDirectInvoice &&
    !claimsTimeLimitExceeded;

  return ENABLE_INVOICE_DETAILS_V2 ? (
    <Grid display="flex" alignSelf="center" gap="12px">
      <Button
        data-testid="download-to-excel-button"
        onClick={onDownloadToExcelClick}
      >
        <DownloadIcon />
        {t("common:table.download.textV2")}
      </Button>
      <InvoiceDownloadButton
        sendRef={sendRef}
        setPrintButtonClicked={setPrintButtonClicked}
      />
      {shouldShowCreateClaimButton && (
        <Button
          variant="primary"
          data-testid="create-claim-button"
          onClick={() =>
            navigate(
              `/claim/create?invoiceNumber=${invoiceNumber}&accountId=${accountId}`
            )
          }
        >
          {t("invoices:allInvoices.dropDown.createClaim")}
        </Button>
      )}
      {shouldShowCreateVendorClaimButton && (
        <Button
          variant="primary"
          data-testid="create-vendor-direct-claim-button"
          onClick={() =>
            navigate(
              `/claim/create?invoiceNumber=${invoiceNumber}&accountId=${accountId}`
            )
          }
        >
          {t("invoices:allInvoices.dropDown.createVendorDirectClaim")}
        </Button>
      )}
    </Grid>
  ) : (
    <></>
  );
};

export default InvoiceDetailsActionButtons;
